import React, { useState } from 'react';
import { Button } from './ui/button';
import { Clock } from './Clock';
import { WindowData } from '../hooks/useOS';
import { useAuth } from '../hooks/useAuth';
import { useAuth as useMainAuth } from '@/contexts/AuthContext';
import { User, LogOut, FileText } from 'lucide-react';
import MapleOSLogo from '@/components/common/MapleOSLogo';
import AICreditStatus from './AICreditStatus';
import { cn } from '../lib/utils';
import { useNavigate } from 'react-router-dom';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel
} from './ui/alert-dialog';
import { PROGRAM_MAP } from '../utils/programs';
import { makeOverrideIcon } from './AppIcon';

const StartButtonIcon = makeOverrideIcon('mapleos-start-color', ({ className }) => (
  <MapleOSLogo iconOnly className={className} />
));

interface TaskbarProps {
  openWindows: WindowData[];
  onWindowClick: (windowId: string) => void;
  onStartClick: () => void;
  onOpenWindow?: (appId: string, title: string) => void;
  isStartMenuOpen: boolean;
  onToggleMinimize?: (windowId: string) => void; // NEW optional prop
  groupButtons?: boolean; // NEW: group taskbar buttons by appId
  iconsOnly?: boolean; // NEW: render icons-only (no labels)
  messengerTrayWindow?: WindowData | null;
  onMessengerTrayClick?: () => void;
}

export const Taskbar: React.FC<TaskbarProps> = ({
  openWindows,
  onWindowClick,
  onStartClick,
  onOpenWindow,
  isStartMenuOpen,
  onToggleMinimize,
  groupButtons = false,
  iconsOnly = false,
  messengerTrayWindow = null,
  onMessengerTrayClick,
}) => {
  const { user, profile } = useAuth();
  const { signOut } = useMainAuth();
  const navigate = useNavigate();
  const [showExitDialog, setShowExitDialog] = useState(false);
  const MessengerIcon = PROGRAM_MAP['messenger']?.icon || FileText;

  const getFirstName = (name?: string | null) =>
    name?.trim()?.split(/\s+/)[0] || null;

  const displayName =
    getFirstName((profile as any)?.full_name) ||
    profile?.username ||
    (user?.email ? user.email.split('@')[0] : '');

  const handleLogout = async () => {
    setShowExitDialog(false);
    await signOut();
    navigate('/auth');
  };

  return (
    <div
      className="fixed bottom-0 right-0 h-12 bg-taskbar text-taskbar-foreground border-t border-border flex items-center px-2 z-[8500]"
      style={{ left: 'var(--mapleos-rail-offset, 0px)' }}
    >
      {/* Start Button */}
      <Button
        variant="taskbar"
        size="sm"
        onClick={onStartClick}
        className={cn(
          'mr-2 font-bold !text-inherit bg-[hsl(var(--taskbar-btn))] hover:bg-[hsl(var(--taskbar-btn-hover))]',
          isStartMenuOpen &&
            'bg-[hsl(var(--taskbar-btn-active))] !text-inherit'
        )}
      >
        <StartButtonIcon size={28} className="mr-1 h-7 w-7 text-current" />
        Start
      </Button>

      {/* Window Buttons */}
      <div className="flex-1 flex gap-1 overflow-x-auto">
        {(() => {
          if (!groupButtons) {
            return openWindows.map((window) => {
              const IconComp = PROGRAM_MAP[window.appId]?.icon || FileText;
              const isMin = window.isMinimized;
              return (
                <Button
                  key={window.id}
                  variant="taskbar"
                  size="sm"
                  onClick={() => {
                    if (isMin) {
                      onWindowClick(window.id);
                    } else {
                      if (onToggleMinimize) {
                        onToggleMinimize(window.id);
                      } else {
                        onWindowClick(window.id);
                      }
                    }
                  }}
                  className={cn(
                    'min-w-0 max-w-40 truncate !text-inherit',
                    isMin
                      ? 'opacity-75 bg-[hsl(var(--taskbar-btn))]'
                      : 'bg-[hsl(var(--taskbar-btn))] hover:bg-[hsl(var(--taskbar-btn-hover))]'
                  )}
                  title={iconsOnly ? window.title : undefined}
                >
                  <IconComp className="text-current" />
                  {!iconsOnly && <span className="truncate">{window.title}</span>}
                </Button>
              );
            });
          }

          const byApp = new Map<string, WindowData[]>();
          for (const w of openWindows) {
            const arr = byApp.get(w.appId) || [];
            arr.push(w);
            byApp.set(w.appId, arr);
          }

          return Array.from(byApp.entries()).map(([appId, windows]) => {
            const IconComp = PROGRAM_MAP[appId]?.icon || FileText;
            const active =
              windows.find((w) => !w.isMinimized) || windows[windows.length - 1];
            const isMin = active?.isMinimized ?? false;
            const label = active?.title || PROGRAM_MAP[appId]?.label || 'Application';

            return (
              <Button
                key={`group-${appId}`}
                variant="taskbar"
                size="sm"
                onClick={() => {
                  if (!active) return;
                  if (isMin) {
                    onWindowClick(active.id);
                  } else {
                    if (onToggleMinimize) {
                      onToggleMinimize(active.id);
                    } else {
                      onWindowClick(active.id);
                    }
                  }
                }}
                className={cn(
                  'min-w-0 max-w-40 truncate !text-inherit',
                  isMin
                    ? 'opacity-75 bg-[hsl(var(--taskbar-btn))]'
                    : 'bg-[hsl(var(--taskbar-btn))] hover:bg-[hsl(var(--taskbar-btn-hover))]'
                )}
                title={iconsOnly ? `${label} (${windows.length})` : undefined}
              >
                <IconComp className="text-current" />
                {!iconsOnly && <span className="truncate">{label}</span>}
              </Button>
            );
          });
        })()}
      </div>

      {/* User Info & System Tray */}
      <div className="flex items-center gap-2 ml-2">
        <AICreditStatus onOpenWindow={onOpenWindow} />

        {messengerTrayWindow && onMessengerTrayClick && (

          <Button
            variant="taskbar"
            size="sm"
            onClick={onMessengerTrayClick}
            className={cn(
              'h-8 w-8 p-0 !text-inherit bg-[hsl(var(--taskbar-btn))] hover:bg-[hsl(var(--taskbar-btn-hover))]',
              !messengerTrayWindow.hiddenInTray && 'bg-[hsl(var(--taskbar-btn-active))]'
            )}
            title={messengerTrayWindow.hiddenInTray ? 'Restore Messenger' : 'Hide Messenger to tray'}
            aria-label={messengerTrayWindow.hiddenInTray ? 'Restore Messenger' : 'Hide Messenger to tray'}
          >
            <MessengerIcon className="h-4 w-4 text-current" />
          </Button>
        )}
        {user && (
          <div className="flex items-center gap-2 px-2 py-1 rounded bg-[hsl(var(--taskbar-btn))] !text-inherit">
            <User size={12} className="text-current" />
            <span className="text-xs font-medium">{displayName}</span>
            <Button
              variant="taskbar"
              size="sm"
              onClick={() => setShowExitDialog(true)}
              className="h-6 w-6 p-0 hover:bg-[hsl(var(--destructive))/0.15] !text-inherit"
              title="Sign Out or Sign Off"
            >
              <LogOut size={10} className="text-current" />
            </Button>
          </div>
        )}
        <div className="!text-inherit">
          <Clock onOpenWindow={onOpenWindow} />
        </div>
      </div>

      <AlertDialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Leave MapleOS?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to log out?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button variant="destructive" onClick={handleLogout}>
              Logout
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};